import { apiBaseUrl, apiBaseSocket } from './config.js';
import { initializeSimulationTimer } from './simulationTimer.js';
import { t, confirmT, updateElementText } from './translationHelper.js';
import translations, { getCompanyI18n } from './translations.js';
globalThis.ZTranslations = translations;

export function showMementoMoriModal() {
  const modal = document.createElement('div');
  modal.classList.add('modal');
  modal.style.cssText = `
	position: fixed !important;
	top: 0 !important;
	left: 0 !important;
	width: 100vw !important;
	height: 100vh !important;
	background: rgba(0, 0, 0, 0.9) !important;
	display: flex !important;
	align-items: center !important;
	justify-content: center !important;
	z-index: 10000 !important;
  `;
  
  modal.innerHTML = `
	<div class="modal-content" style="background: #000; color: #fff; padding: 30px; border-radius: 10px; max-width: 600px; text-align: center;">
	  <h2 style="color: #ff6666; margin-bottom: 20px;">${t('modals.memento_mori_title', ' Memento Mori Simulation ')}</h2>
	  <p class="memento-intro">${t('modals.memento_mori_description', 'Remember you must die. This simulation explores meaningful choices in limited time.')}</p>

	  <!-- Three reflection questions -->
	  <div style="text-align: left; margin: 20px 0;">
		<p><strong>${t('memento.question_1', '1. How do you feel about your life right now?')}</strong></p>
		<textarea id="mmEmotionalState" placeholder="${t('memento.question_1_placeholder', 'e.g., anxious about the future, grateful for relationships...')}" style="width: 100%; min-height: 60px; margin-bottom: 15px;"></textarea>

		<p><strong>${t('memento.question_2', '2. What matters most to you?')}</strong></p>
		<textarea id="mmIntention" placeholder="${t('memento.question_2_placeholder', 'e.g., family, creativity, making an impact...')}" style="width: 100%; min-height: 60px; margin-bottom: 15px;"></textarea>

		<p><strong>${t('memento.question_3', '3. If your life ended tomorrow, what would you want your legacy to represent?')}</strong></p>
		<textarea id="mmPortfolioMeaning" placeholder="${t('memento.question_3_placeholder', 'e.g., someone who helped others, lived authentically...')}" style="width: 100%; min-height: 60px;"></textarea>
	  </div>

	  <button id="proceedToCompanies" class="choice-btn" style="margin-top: 20px;">${t('memento.find_companies_button', 'Find Meaningful Companies')}</button>
	  <button id="closeMementoModal" class="secondary-btn">Cancel</button>
	</div>
  `;
  
  document.body.appendChild(modal);

  document.getElementById('proceedToCompanies').addEventListener('click', async () => {
	const emotionalState = document.getElementById('mmEmotionalState').value.trim();
	const intention = document.getElementById('mmIntention').value.trim();
	const portfolioMeaning = document.getElementById('mmPortfolioMeaning').value.trim();

	if (!emotionalState || !intention || !portfolioMeaning) {
	  return window.alert(t('validation.complete_reflections', 'Please complete all three reflections.'));
	}

	// Store reflections temporarily
	sessionStorage.setItem('mmReflections', JSON.stringify({
	  emotionalState,
	  intention,
	  portfolioMeaning
	}));

	modal.remove();
	await showMementoCompanySelection(emotionalState, intention, portfolioMeaning);
  });

  document.getElementById('closeMementoModal').addEventListener('click', () => {
	modal.remove();
  });
}


async function showMementoCompanySelection(emotionalState, intention, portfolioMeaning) {
  const loadingModal = document.createElement('div');
  loadingModal.classList.add('modal');
  loadingModal.style.cssText = `
	position: fixed !important;
	top: 0 !important;
	left: 0 !important;
	width: 100vw !important;
	height: 100vh !important;
	background: rgba(0, 0, 0, 0.95) !important;
	display: flex !important;
	align-items: center !important;
	justify-content: center !important;
	z-index: 10001 !important;
  `;
  loadingModal.innerHTML = `
	<div class="modal-content">
	  <h2>${t('simulation.analyzing_life_values', 'Analyzing your life values...')}</h2>
	  <p>${t('simulation.finding_companies', 'Finding companies that align with your legacy...')}</p>
	</div>
  `;
  document.body.appendChild(loadingModal);

  try {
	// Use your existing psychology matching
	const response = await fetch(`${apiBaseUrl}/api/company/generate-company-profile`, {
	  method: 'POST',
	  headers: { 
		'Content-Type': 'application/json',
		...(localStorage.getItem("token") && {
		  Authorization: `Bearer ${localStorage.getItem("token")}`
		})
	  },
	  body: JSON.stringify({ 
		responseQ1: emotionalState, 
		responseQ2: intention, 
		responseQ3: portfolioMeaning 
	  })
	});

	if (!response.ok) throw new Error('Failed to find companies');

	const result = await response.json();
	const companies = result.companies || [];

	if (companies.length === 0) {
	  loadingModal.remove();
	  window.alert(t('messages.no_companies_found', 'No matching companies found. Please try different reflections.'));
	  return;
	}

	loadingModal.remove();
	showMementoCompanyPicker(companies, { emotionalState, intention, portfolioMeaning });

  } catch (error) {
	loadingModal.remove();
	console.error('Company selection error:', error);
	window.alert(t('messages.error', `Error finding companies: ${error.message}`));
  }
}


function showMementoCompanyPicker(companies, reflections) {
  const modal = document.createElement('div');
  modal.classList.add('modal');
  modal.style.cssText = `
	position: fixed !important;
	top: 0 !important;
	left: 0 !important;
	width: 100vw !important;
	height: 100vh !important;
	background: rgba(0, 0, 0, 0.9) !important;
	display: flex !important;
	align-items: center !important;
	justify-content: center !important;
	z-index: 10001 !important;
	overflow-y: auto !important;
  `;

  const selectedCompanies = new Set();
  const outputLabel = t('labels.output', 'Output');
  
  const companiesHtml = companies.slice(0, 10).map((c, idx) => {
	const rowId = (c.csvRow ?? c.Id ?? c.ID ?? '').toString().trim();
	const name = rowId ? t(`companies_by_id.${rowId}.Company_Name`, c['Company Name'] || c.name) : (c['Company Name'] || c.name);
	const output = rowId ? t(`companies_by_id.${rowId}.Out_put`, c['Out put'] || c.output) : (c['Out put'] || c.output);
	const desc = rowId ? t(`companies_by_id.${rowId}.Description`, c.description) : c.description;

	return `
	  <div class="memento-company-card" data-index="${idx}" style="
		border: 2px solid #333;
		padding: 15px;
		margin: 10px 0;
		border-radius: 8px;
		cursor: pointer;
		transition: all 0.3s;
	  ">
		<input type="checkbox" class="company-checkbox" data-index="${idx}" style="margin-right: 10px; transform: scale(1.2);">
		<strong style="color: #ff6666;">${name}</strong>
		<p style="color: #ccc; margin: 5px 0;"><strong>${outputLabel}:</strong> ${output}</p>
		<p style="color: #999; font-size: 0.9em;">${desc}</p>
	  </div>
	`;
  }).join('');

  modal.innerHTML = `
	<div class="modal-content" style="background: #000; color: #fff; padding: 30px; border-radius: 10px; max-width: 700px; max-height: 90vh; overflow-y: auto;">
	  <h2 style="color: #ff6666;">${t('memento.choose_legacy_title', 'Choose Your Legacy Portfolio')}</h2>
	  <p style="color: #ccc;">${t('memento.choose_legacy_description', 'Select 3-5 companies that represent what matters most to you.')}</p>
	  <p style="color: #ff9900; font-size: 0.9em; margin-bottom: 20px;">${t('memento.choose_legacy_warning', 'These will be the stocks you hold when your time expires.')}</p>

	  <div id="companyList">
		${companiesHtml}
	  </div>

	  <div style="margin-top: 20px; padding-top: 20px; border-top: 1px solid #333;">
		<p id="selectionCount" style="color: #ff6666;">${t('memento.selected_count', 'Selected')}: 0 / 5</p>
		<button id="confirmSelection" class="choice-btn" disabled style="opacity: 0.5;">Enter Mortality Chamber</button>
		<button id="cancelSelection" class="secondary-btn">Cancel</button>
	  </div>
	</div>
  `;

  document.body.appendChild(modal);

  // Handle company selection
  const checkboxes = modal.querySelectorAll('.company-checkbox');
  const countDisplay = document.getElementById('selectionCount');
  const confirmBtn = document.getElementById('confirmSelection');

  checkboxes.forEach(checkbox => {
	checkbox.addEventListener('change', (e) => {
	  const idx = parseInt(e.target.dataset.index);
	  
	  if (e.target.checked) {
		if (selectedCompanies.size >= 5) {
		  e.target.checked = false;
		  return window.alert(t('validation.max_companies', 'Maximum 5 companies allowed'));
		}
		selectedCompanies.add(idx);
	  } else {
		selectedCompanies.delete(idx);
	  }

	  countDisplay.textContent = `${t('memento.selected_count', 'Selected')}: ${selectedCompanies.size} / 5`;
	  
	  if (selectedCompanies.size >= 3 && selectedCompanies.size <= 5) {
		confirmBtn.disabled = false;
		confirmBtn.style.opacity = '1';
	  } else {
		confirmBtn.disabled = true;
		confirmBtn.style.opacity = '0.5';
	  }
	});
  });

  document.getElementById('confirmSelection').addEventListener('click', async () => {
	const selectedCompanyData = Array.from(selectedCompanies).map(idx => companies[idx]);
	
	// Store everything we need for session creation
	sessionStorage.setItem('mmSelectedCompanies', JSON.stringify(selectedCompanyData));
	
	modal.remove();
	await createMementoMoriSession(reflections, selectedCompanyData);
  });

  document.getElementById('cancelSelection').addEventListener('click', () => {
	modal.remove();
  });
}


async function createMementoMoriSession(reflections, selectedCompanies) {
  const loadingModal = document.createElement('div');
  loadingModal.classList.add('modal');
  loadingModal.style.cssText = `
	position: fixed !important;
	top: 0 !important;
	left: 0 !important;
	width: 100vw !important;
	height: 100vh !important;
	background: rgba(0, 0, 0, 0.95) !important;
	display: flex !important;
	align-items: center !important;
	justify-content: center !important;
	z-index: 10002 !important;
  `;
  loadingModal.innerHTML = `
	<div class="modal-content">
	  <h2>${t('simulation.building_chamber', 'Building your mortality chamber...')}</h2>
	  <p>${t('simulation.preparing_stocks', 'Preparing your legacy portfolio...')}</p>
	</div>
  `;
  document.body.appendChild(loadingModal);

  try {
	const preferredLanguage = localStorage.getItem('preferredLanguage') || 'en';
	
	const response = await fetch(`${apiBaseUrl}/api/sim/createMementoMoriSession`, {
	  method: 'POST',
	  headers: {
		'Content-Type': 'application/json',
		...(localStorage.getItem("token") && {
		  Authorization: `Bearer ${localStorage.getItem("token")}`
		})
	  },
	  body: JSON.stringify({
		mementoMoriData: reflections,
		selectedCompanies: selectedCompanies,  // ← NEW: companies they chose
		startBotAutomation: true,
		preferredLanguage
	  })
	});

	if (!response.ok) {
	  const error = await response.json();
	  throw new Error(error.error || 'Failed to create session');
	}

	const result = await response.json();

	// Store session data
	localStorage.setItem('portfolioId', result.portfolioId);
	localStorage.setItem('sessionId', result.sessionId);
	localStorage.setItem('token', result.token);
	localStorage.setItem('role', result.role || 'user');
	localStorage.setItem('simulationType', 'memento-mori');
	localStorage.setItem('isSimulated', 'true');
	
	if (result.mementoMoriObjectiveId) {
	  localStorage.setItem('mementoMoriObjectiveId', result.mementoMoriObjectiveId);
	}
	if (result.simulationEndTime) {
	  localStorage.setItem('simulationEndTime', result.simulationEndTime);
	}
	if (result.simulationTimerId) {
	  localStorage.setItem('simulationTimerId', result.simulationTimerId);
	}

	loadingModal.innerHTML = `
	  <div class="modal-content" style="background: #000; color: #0f0; padding: 40px; border-radius: 10px;">
		<h2 style="color: #00ff00;">${t('memento.your_time_begins', ' Your Time Begins Now ')}</h2>
		<p style="color: #ccc;">${t('memento.entering_mortality_chamber', 'Entering your mortality chamber in 2 seconds...')}</p>
	  </div>
	`;

	setTimeout(() => {
	  loadingModal.remove();
	  window.location.href = 'sim.html';
	}, 2000);

  } catch (error) {
	loadingModal.remove();
	console.error('Session creation error:', error);
	window.alert(t('messages.error', `Failed to create session: ${error.message}`));
  }
}

export async function initializeMementoMoriUI() {
  const simulationType = localStorage.getItem('simulationType');
  console.log('🪦 [initializeMementoMoriUI] Called. SimulationType:', simulationType);

  if (simulationType !== 'memento-mori') {
    console.log('⚠️ [initializeMementoMoriUI] Not memento-mori mode, exiting');
    return;
  }


  // ✅ Initialize objective if not already set
  await initializeMementoMoriObjective();

  // Clear any existing interval to prevent duplicates
  if (window.mementoEulogyInterval) {
    console.log('🗑️ [initializeMementoMoriUI] Clearing existing eulogy interval');
    clearInterval(window.mementoEulogyInterval);
    window.mementoEulogyInterval = null;
  }

  // Remove any existing display
  const existing = document.getElementById('memento-mori-eulogy');
  if (existing) {
    console.log('🗑️ [initializeMementoMoriUI] Removing existing eulogy display');
    existing.remove();
  }

  // Create eulogy preview display - compact bar at top of screen
  const eulogyDisplay = document.createElement('div');
  eulogyDisplay.id = 'memento-mori-eulogy';
  eulogyDisplay.style.cssText = `
	position: fixed;
	top: 0;
	left: 0;
	right: 0;
	background: rgba(0, 0, 0, 0.9);
	border-bottom: 1px solid #ff6666;
	padding: 6px 12px;
	color: #fff;
	z-index: 9998;
	box-shadow: 0 2px 10px rgba(255, 102, 102, 0.3);
	font-family: 'Courier New', monospace;
	cursor: pointer;
	transition: all 0.3s ease;
  `;

  eulogyDisplay.innerHTML = `
	<div style="display: flex; align-items: center; justify-content: space-between; max-width: 668px; margin: 0 auto;">
	  <span style="color: #ff6666; font-size: 0.65em; font-weight: bold; letter-spacing: 1px; white-space: nowrap;">
		${t('memento.your_eulogy_label', 'YOUR EULOGY')}
	  </span>
	  <div id="eulogy-preview-text" style="
		flex: 1;
		font-size: 0.75em;
		color: #ccc;
		font-style: italic;
		margin: 0 10px;
		white-space: nowrap;
		overflow: hidden;
		text-overflow: ellipsis;
	  ">
		${t('memento.generating_eulogy', 'Generating your life\'s story based on your choices...')}
	  </div>
	  <span style="color: #ff6666; font-size: 0.8em; opacity: 0.7;">&#9660;</span>
	</div>
  `;

  // Add click handler to expand
  eulogyDisplay.addEventListener('click', () => {
	showExpandedEulogy();
  });

  // Add hover effect
  eulogyDisplay.addEventListener('mouseenter', () => {
	eulogyDisplay.style.boxShadow = '0 4px 15px rgba(255, 102, 102, 0.5)';
	eulogyDisplay.style.background = 'rgba(20, 0, 0, 0.95)';
  });

  eulogyDisplay.addEventListener('mouseleave', () => {
	eulogyDisplay.style.boxShadow = '0 2px 10px rgba(255, 102, 102, 0.3)';
	eulogyDisplay.style.background = 'rgba(0, 0, 0, 0.9)';
  });

  document.body.appendChild(eulogyDisplay);
  console.log('✅ [initializeMementoMoriUI] Eulogy display added to page');

  // Only fetch immediately if this is the first initialization
  if (!window.mementoMoriInitialized) {
    console.log('⏰ [initializeMementoMoriUI] First initialization - setting up initial eulogy fetch in 500ms');
    setTimeout(() => {
      console.log('🔄 [initializeMementoMoriUI] Initial eulogy fetch triggered');
      fetchAndUpdateEulogy();
    }, 500);
    window.mementoMoriInitialized = true;
  } else {
    console.log('ℹ️ [initializeMementoMoriUI] Already initialized - skipping immediate fetch');
  }

  // Update eulogy every 2 minutes (120000ms)
  console.log('⏰ [initializeMementoMoriUI] Setting up 2-minute interval for eulogy updates');
  window.mementoEulogyInterval = setInterval(() => {
    console.log('🔄 [initializeMementoMoriUI] Interval-triggered eulogy update');
    fetchAndUpdateEulogy();
  }, 120000);

  console.log('✅ [initializeMementoMoriUI] Initialization complete');
}

// Initialize or get existing memento mori objective
async function initializeMementoMoriObjective() {
  try {
	// Check if we already have an objectiveId
	const existingObjectiveId = localStorage.getItem('mementoMoriObjectiveId');
	if (existingObjectiveId) {
	  console.log('✅ [initializeMementoMoriObjective] ObjectiveId already exists:', existingObjectiveId);
	  return existingObjectiveId;
	}

	// Get portfolioId and sessionId
	const portfolioId = localStorage.getItem('portfolioId');
	const sessionId = localStorage.getItem('sessionId');
	const token = localStorage.getItem('token');

	console.log('🔄 [initializeMementoMoriObjective] Calling initialize endpoint...');

	const response = await fetch(`${apiBaseUrl}/api/memento-mori/initialize`, {
	  method: 'POST',
	  headers: {
		'Content-Type': 'application/json',
		...(token && { Authorization: `Bearer ${token}` })
	  },
	  body: JSON.stringify({ portfolioId, sessionId })
	});

	if (!response.ok) {
	  throw new Error(`Failed to initialize objective: ${response.status}`);
	}

	const data = await response.json();
	console.log('✅ [initializeMementoMoriObjective] Response:', data);

	if (data.success && data.objectiveId) {
	  localStorage.setItem('mementoMoriObjectiveId', data.objectiveId);
	  console.log('💾 [initializeMementoMoriObjective] Stored objectiveId:', data.objectiveId);
	  return data.objectiveId;
	}

  } catch (error) {
	console.error('❌ [initializeMementoMoriObjective] Error:', error);
  }
}

// Fetch eulogy from backend and update preview
async function fetchAndUpdateEulogy() {
  console.log('🪦 [fetchAndUpdateEulogy] Called at', new Date().toISOString());

  try {
	const objectiveId = localStorage.getItem('mementoMoriObjectiveId');
	console.log('🔍 [fetchAndUpdateEulogy] ObjectiveId from localStorage:', objectiveId);

	if (!objectiveId) {
	  console.error('❌ [fetchAndUpdateEulogy] No memento mori objective ID found in localStorage');
	  console.log('📋 [fetchAndUpdateEulogy] All localStorage keys:', Object.keys(localStorage));
	  return;
	}

	const token = localStorage.getItem("token");
	console.log('🔐 [fetchAndUpdateEulogy] Has auth token:', !!token);

	// Add timeout to prevent hanging on slow LLM responses
	const controller = new AbortController();
	const timeoutId = setTimeout(() => controller.abort(), 10000); // 10s timeout for periodic fetch

	console.log('🌐 [fetchAndUpdateEulogy] Making API call to:', `${apiBaseUrl}/api/memento-mori/current-eulogy`);
	const response = await fetch(`${apiBaseUrl}/api/memento-mori/current-eulogy`, {
	  method: 'POST',
	  headers: {
		'Content-Type': 'application/json',
		...(token && {
		  Authorization: `Bearer ${token}`
		})
	  },
	  body: JSON.stringify({ objectiveId }),
	  signal: controller.signal
	});

	clearTimeout(timeoutId);
	console.log('📡 [fetchAndUpdateEulogy] Response status:', response.status, response.statusText);

	if (!response.ok) {
	  const errorText = await response.text();
	  console.error('❌ [fetchAndUpdateEulogy] Response error:', errorText);
	  throw new Error(`Failed to fetch eulogy: ${response.status} ${errorText}`);
	}

	const data = await response.json();
	console.log('✅ [fetchAndUpdateEulogy] Response data received:', {
	  success: data.success,
	  hasEulogyText: !!data.eulogyText,
	  eulogyLength: data.eulogyText?.length || 0,
	  eulogyPreview: data.eulogyText?.substring(0, 100)
	});

	updateEulogyPreview(data.eulogyText);

	// Store full eulogy for expanded view
	window.currentEulogyText = data.eulogyText;
	console.log('💾 [fetchAndUpdateEulogy] Stored eulogy text in window.currentEulogyText');

  } catch (error) {
	if (error.name === 'AbortError') {
	  console.warn('⏰ [fetchAndUpdateEulogy] Request timed out (LLM latency)');
	} else {
	  console.error('❌ [fetchAndUpdateEulogy] Error:', error);
	  console.error('❌ [fetchAndUpdateEulogy] Error stack:', error.stack);
	}
	// Use fallback text and store it so it's available at end
	const fallbackText = t('memento.eulogy_fallback', "Your journey through life's marketplace continues to unfold, each choice revealing the values that guide your path.");
	console.log('⚠️ [fetchAndUpdateEulogy] Using fallback text');
	updateEulogyPreview(fallbackText);
	// Store fallback so window.currentEulogyText is never empty
	if (!window.currentEulogyText) {
	  window.currentEulogyText = fallbackText;
	  console.log('💾 [fetchAndUpdateEulogy] Stored fallback in window.currentEulogyText');
	}
  }
}

// Update the preview text in the collapsed popup
function updateEulogyPreview(eulogyText) {
  const previewEl = document.getElementById('eulogy-preview-text');
  if (previewEl) {
	previewEl.textContent = eulogyText;
  }
}

// Show expanded eulogy modal
function showExpandedEulogy() {
  const eulogyText = window.currentEulogyText || t('memento.eulogy_fallback', "Your journey through life's marketplace continues to unfold, each choice revealing the values that guide your path.");

  // Remove any existing expanded view
  const existing = document.getElementById('expanded-eulogy-modal');
  if (existing) existing.remove();

  const modal = document.createElement('div');
  modal.id = 'expanded-eulogy-modal';
  modal.style.cssText = `
	position: fixed;
	top: 0;
	left: 0;
	width: 100vw;
	height: 100vh;
	background: rgba(0, 0, 0, 0.9);
	display: flex;
	align-items: center;
	justify-content: center;
	z-index: 10000;
	animation: fadeIn 0.3s ease;
  `;

  modal.innerHTML = `
	<div style="
	  background: linear-gradient(135deg, #1a0000 0%, #330000 100%);
	  border: 2px solid #ff6666;
	  border-radius: 15px;
	  padding: 30px;
	  max-width: 700px;
	  max-height: 80vh;
	  overflow-y: auto;
	  box-shadow: 0 0 40px rgba(255, 102, 102, 0.5);
	  color: #fff;
	  font-family: 'Courier New', monospace;
	">
	  <div style="
		text-align: center;
		margin-bottom: 25px;
	  ">
		<h2 style="
		  color: #ff6666;
		  font-size: 1.8em;
		  margin: 0 0 10px 0;
		  text-shadow: 0 0 10px rgba(255, 102, 102, 0.5);
		">
		  ${t('memento.your_eulogy_expanded_title', 'YOUR EULOGY')}
		</h2>
		<div style="
		  color: #999;
		  font-size: 0.9em;
		  font-style: italic;
		">
		  ${t('memento.eulogy_subtitle', 'A reflection of your choices in the marketplace of life')}
		</div>
	  </div>

	  <div style="
		background: rgba(0, 0, 0, 0.5);
		border-left: 3px solid #ff6666;
		padding: 25px;
		margin: 20px 0;
		border-radius: 8px;
	  ">
		<p id="expanded-eulogy-text" style="
		  color: #e8e8e8;
		  line-height: 2;
		  font-size: 1.15em;
		  margin: 0;
		  font-style: italic;
		  text-align: left;
		  font-family: Georgia, 'Times New Roman', serif;
		  letter-spacing: 0.3px;
		  word-spacing: 2px;
		">
		  ${eulogyText}
		</p>
	  </div>

	  <div style="
		text-align: center;
		margin-top: 25px;
		padding-top: 20px;
		border-top: 1px solid #444;
	  ">
		<p style="
		  color: #999;
		  font-size: 0.85em;
		  margin-bottom: 15px;
		">
		  ${t('memento.eulogy_update_info', 'This eulogy updates every 2 minutes based on your trading actions')}
		</p>
		<button id="close-expanded-eulogy" style="
		  background: rgba(255, 102, 102, 0.2);
		  color: #ff6666;
		  border: 1px solid #ff6666;
		  padding: 10px 25px;
		  border-radius: 5px;
		  cursor: pointer;
		  font-family: 'Courier New', monospace;
		  font-size: 1em;
		  transition: all 0.3s;
		" onmouseover="this.style.background='#ff6666'; this.style.color='#000';"
		   onmouseout="this.style.background='rgba(255, 102, 102, 0.2)'; this.style.color='#ff6666';">
		  Close
		</button>
	  </div>
	</div>
  `;

  document.body.appendChild(modal);

  // Close on button click
  document.getElementById('close-expanded-eulogy').addEventListener('click', () => {
	modal.remove();
  });

  // Close on background click
  modal.addEventListener('click', (e) => {
	if (e.target === modal) {
	  modal.remove();
	}
  });
}

// Legacy function - kept for compatibility but now unused
export function updateEulogyValue(portfolioData) {
  // This function is no longer used with the new eulogy system
  // Kept for backward compatibility
  console.log('Legacy updateEulogyValue called - using new eulogy system instead');
}

// Call this whenever portfolio changes - trigger eulogy refresh
export function syncEulogyWithPortfolio(portfolioData) {
  window.currentPortfolio = portfolioData;
  // Immediately fetch updated eulogy after portfolio change
  if (localStorage.getItem('simulationType') === 'memento-mori') {
	fetchAndUpdateEulogy();
  }
}